import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Music, Search, Plus, Trash2, Calendar, Check, X, Edit2,
  BookOpen, ChevronLeft, ChevronRight, Save, RefreshCw,
  Users, UserCheck, UserX, Clock, MapPin, FileText, Eye, Mail,
  QrCode, Download, AlertTriangle, Award, Loader2, ShieldCheck
} from 'lucide-react';
import DepartmentEmailModule from './DepartmentEmailModule';
import { QRCodeSVG } from 'qrcode.react';
import ChoirEventsTab from './choir/ChoirEventsTab';
import ChoirAttendanceTab from './choir/ChoirAttendanceTab';
import ChoirVobApplicationsTab from './choir/ChoirVobApplicationsTab';
import ChoirHymnsTab from './choir/ChoirHymnsTab';
import ChoirMasterPasswordTab from './choir/ChoirMasterPasswordTab';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const SERVICE_TYPES = [
  { value: 'sunday', label: 'Sunday Service', hymnCount: 7, color: 'amber' },
  { value: 'weekly', label: 'Weekday Service', hymnCount: 5, color: 'blue' },
  { value: 'new_moon', label: 'New Moon (1st Thursday)', hymnCount: 4, color: 'purple' },
];

const VOICE_PARTS = ['soprano', 'alto', 'tenor', 'bass'];
const INSTRUMENTS = ['piano', 'organ', 'guitar', 'drums', 'saxophone', 'trumpet', 'violin', 'flute', 'none', 'other'];

/**
 * PasswordEmailBadge — shows whether the most recent master-password email
 * actually reached this choir member.
 *
 * States:
 *  • no email on file  → grey "—"
 *  • never sent        → grey "Never sent"
 *  • stale (rotation happened after their last delivery) → amber "Stale"
 *  • sent              → green check + relative date
 *  • failed            → red X + reason on hover
 */
const PasswordEmailBadge = ({ member }) => {
  const formatRel = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const days = Math.floor(diffMs / 86400000);
      if (days <= 0) return 'today';
      if (days === 1) return '1d ago';
      if (days < 30) return `${days}d ago`;
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  if (!member.email) {
    return <span className="text-[11px] text-slate-400" title="No email on file">—</span>;
  }
  const status = member.password_email_status;
  const at = member.password_email_at;
  if (!at) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" title="No master-password email delivery recorded yet">
        Never sent
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full"
        title={member.password_email_error || 'Email delivery failed'}
      >
        ✕ Failed · {formatRel(at)}
      </span>
    );
  }
  if (member.password_email_stale) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full"
        title="A newer master password has been set since this delivery — this member missed the most recent rotation."
      >
        ⚠ Stale · {formatRel(at)}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full"
      title={`Master password email delivered · ${new Date(at).toLocaleString('en-GB')}`}
    >
      ✓ {formatRel(at)}
    </span>
  );
};

export function ChoirMinistrySection({ adminUser }) {
  const [activeTab, setActiveTab] = useState('approved');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Data states
  const [members, setMembers] = useState([]);
  const [rehearsals, setRehearsals] = useState([]);
  const [songs, setSongs] = useState([]);
  const [hymns, setHymns] = useState([]);
  const [selections, setSelections] = useState([]);
  const [vobApplications, setVobApplications] = useState([]);

  // Approved members + events + attendance states
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [choirEvents, setChoirEvents] = useState([]);
  const [choirAlerts, setChoirAlerts] = useState([]);
  const [newEventType, setNewEventType] = useState('rehearsal');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventTime, setNewEventTime] = useState('18:00');
  const [newEventTitle, setNewEventTitle] = useState('');
  const [eventCreating, setEventCreating] = useState(false);
  const [selectedEventAttendance, setSelectedEventAttendance] = useState(null);
  const [eventAttendanceRecords, setEventAttendanceRecords] = useState([]);
  const [venueToken, setVenueToken] = useState(null);
  const [rotatingVenueToken, setRotatingVenueToken] = useState(false);
  
  // Form states
  const [showRehearsalForm, setShowRehearsalForm] = useState(false);
  const [showSongForm, setShowSongForm] = useState(false);
  const [showHymnForm, setShowHymnForm] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  
  // Hymn selection states
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [serviceType, setServiceType] = useState('sunday');
  const [serviceDate, setServiceDate] = useState('');
  const [selectedHymns, setSelectedHymns] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Form drafts
  const [rehearsalDraft, setRehearsalDraft] = useState({ date: '', time: '', location: '', notes: '' });
  const [songDraft, setSongDraft] = useState({ title: '', composer: '', key: '', lead: '', notes: '', lyrics: '' });
  const [hymnDraft, setHymnDraft] = useState({ number: '', title: '', first_line: '', verses: '', language: 'english' });

  const isMainAdmin = adminUser?.role === 'main_admin';
  const currentServiceConfig = SERVICE_TYPES.find(s => s.value === serviceType);
  const requiredHymnCount = currentServiceConfig?.hymnCount || 5;

  // Master password rotation state (Main Admin only)
  const [masterPasswordStatus, setMasterPasswordStatus] = useState(null);
  const [masterPasswordModalDismissed, setMasterPasswordModalDismissed] = useState(false);
  const masterPasswordDue = !!(isMainAdmin && masterPasswordStatus?.is_due);

  useEffect(() => {
    if (!isMainAdmin) return;
    const t = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || '';
    if (!t) return;
    axios.get(`${API}/auth/member/admin/choir-master-password/status`, {
      headers: { 'x-admin-token': t },
    }).then(r => setMasterPasswordStatus(r.data)).catch(() => {});
  }, [isMainAdmin, activeTab]);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (activeTab === 'hymns') {
      loadHymns();
    }
  }, [searchTerm, currentPage, activeTab]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [membersRes, rehearsalsRes, songsRes, statsRes, selectionsRes, vobRes,
             approvedRes, eventsRes, alertsRes, venueRes] = await Promise.all([
        axios.get(`${API}/choir/members`),
        axios.get(`${API}/choir/rehearsals`),
        axios.get(`${API}/choir/songs`),
        axios.get(`${API}/choir/hymns/stats`),
        axios.get(`${API}/choir/service-hymns`),
        axios.get(`${API}/choir/vob-applications`),
        axios.get(`${API}/choir/approved-members`),
        axios.get(`${API}/choir/events`),
        axios.get(`${API}/choir/alerts`),
        axios.get(`${API}/choir/venue-token`),
      ]);
      setMembers(membersRes.data);
      setRehearsals(rehearsalsRes.data);
      setSongs(songsRes.data);
      setStats(statsRes.data);
      setSelections(selectionsRes.data);
      setVobApplications(vobRes.data);
      setApprovedMembers(approvedRes.data || []);
      setChoirEvents(eventsRes.data || []);
      setChoirAlerts(alertsRes.data?.alerts || []);
      setVenueToken(venueRes.data?.token || null);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const refreshChoirData = async () => {
    try {
      const [approvedRes, eventsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/choir/approved-members`),
        axios.get(`${API}/choir/events`),
        axios.get(`${API}/choir/alerts`),
      ]);
      setApprovedMembers(approvedRes.data || []);
      setChoirEvents(eventsRes.data || []);
      setChoirAlerts(alertsRes.data?.alerts || []);
    } catch (e) { console.error(e); }
  };

  const createChoirEvent = async () => {
    if (!newEventDate) { alert('Please pick a date'); return; }
    setEventCreating(true);
    try {
      await axios.post(`${API}/choir/events`, {
        type: newEventType,
        date: newEventDate,
        start_time: newEventTime,
        title: newEventTitle || undefined,
      });
      setNewEventDate(''); setNewEventTitle('');
      await refreshChoirData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create event');
    }
    setEventCreating(false);
  };

  const deleteChoirEvent = async (eventId) => {
    if (!window.confirm('Delete this event and all its attendance records?')) return;
    try {
      await axios.delete(`${API}/choir/events/${eventId}`);
      await refreshChoirData();
    } catch (e) { alert('Failed to delete event'); }
  };

  const sendEventReminder = async (eventId, eventLabel) => {
    if (!window.confirm(
      `Send the pre-event reminder + check-in link to all approved choir members now for "${eventLabel}"?\n\n` +
      `Members already reminded for this event will be skipped automatically.`
    )) return;
    try {
      const res = await axios.post(`${API}/choir/events/${eventId}/send-reminder`);
      const d = res.data;
      alert(
        `✅ Pre-event reminder sent.\n\n` +
        `• Sent: ${d.sent}\n` +
        `• Skipped (already reminded): ${d.already_reminded}\n` +
        `• Failed: ${d.failed}\n` +
        `• Total members with email: ${d.total_members_with_email}`
      );
    } catch (e) { alert(e.response?.data?.detail || 'Failed to send reminder'); }
  };

  const rotateVenueToken = async () => {
    if (!window.confirm('Regenerate the QR code? Old printed QRs at the church will stop working — you must reprint and redisplay the new one.')) return;
    setRotatingVenueToken(true);
    try {
      const res = await axios.post(`${API}/choir/venue-token/rotate`);
      setVenueToken(res.data.token);
      alert('New QR generated. Please reprint and replace the old QR at the church.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to rotate token');
    }
    setRotatingVenueToken(false);
  };

  const viewEventAttendance = async (event) => {
    try {
      const res = await axios.get(`${API}/choir/attendance/by-event/${event.id}`);
      setEventAttendanceRecords(res.data || []);
      setSelectedEventAttendance(event);
    } catch (e) { alert('Failed to load attendance'); }
  };

  const downloadAttendanceReport = () => {
    window.open(`${API}/choir/attendance/report?format=csv`, '_blank');
  };

  const approveAndEnrollChoir = async (appId) => {
    try {
      await axios.post(`${API}/choir/vob-applications/${appId}/approve-and-enroll`);
      // Refresh vob apps + approved members
      const [vobRes, approvedRes] = await Promise.all([
        axios.get(`${API}/choir/vob-applications`),
        axios.get(`${API}/choir/approved-members`),
      ]);
      setVobApplications(vobRes.data);
      setApprovedMembers(approvedRes.data || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const loadHymns = async () => {
    try {
      const res = await axios.get(`${API}/choir/hymns?search=${searchTerm}&page=${currentPage}&limit=50`);
      setHymns(res.data.hymns);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      console.error('Error loading hymns:', error);
    }
  };

  // Member management
  const approveMember = async (memberId) => {
    try {
      await axios.put(`${API}/choir/members/${memberId}?admin_name=${adminUser?.full_name || 'Admin'}`, { status: 'approved' });
      setMembers(members.map(m => m.id === memberId ? { ...m, status: 'approved' } : m));
    } catch (error) {
      console.error('Error approving member:', error);
    }
  };

  const rejectMember = async (memberId) => {
    try {
      await axios.put(`${API}/choir/members/${memberId}`, { status: 'rejected' });
      setMembers(members.map(m => m.id === memberId ? { ...m, status: 'rejected' } : m));
    } catch (error) {
      console.error('Error rejecting member:', error);
    }
  };

  const deleteMember = async (memberId) => {
    if (!window.confirm('Delete this choir member?')) return;
    try {
      await axios.delete(`${API}/choir/members/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  // VOB Form Application management
  const approveVobApp = async (appId) => {
    try {
      // Use the approve-and-enroll endpoint which also adds them to approved_members
      await axios.post(`${API}/choir/vob-applications/${appId}/approve-and-enroll`);
      setVobApplications(prev => prev.map(a => a.id === appId ? { ...a, status: 'accepted' } : a));
      // Refresh approved members list so the new enrollee appears
      const approvedRes = await axios.get(`${API}/choir/approved-members`);
      setApprovedMembers(approvedRes.data || []);
    } catch (error) { console.error('Error approving VOB application:', error); }
  };

  const declineVobApp = async (appId) => {
    try {
      await axios.post(`${API}/choir/vob-applications/${appId}/decline`);
      setVobApplications(prev => prev.map(a => a.id === appId ? { ...a, status: 'declined' } : a));
    } catch (error) { console.error('Error declining VOB application:', error); }
  };

  const deleteVobApp = async (appId) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await axios.delete(`${API}/choir/vob-applications/${appId}`);
      setVobApplications(prev => prev.filter(a => a.id !== appId));
    } catch (error) { console.error('Error deleting VOB application:', error); }
  };

  const [bulkEnrolling, setBulkEnrolling] = useState(false);
  const bulkEnrollAllVobApps = async () => {
    const pendingOrAccepted = vobApplications.filter(a => a.status !== 'declined').length;
    if (!window.confirm(
      `Enroll ALL ${pendingOrAccepted} non-declined VOB application(s) as Approved Choir Members?\n\n` +
      `• Marks each application as accepted\n` +
      `• Adds them to "Approved Members" (for QR attendance)\n` +
      `• Adds them to the main Church Members list\n` +
      `• Sends password-setup email to new members\n\n` +
      `Safe to re-run — already-enrolled people will be skipped.`
    )) return;
    setBulkEnrolling(true);
    try {
      const res = await axios.post(`${API}/choir/vob-applications/bulk-enroll-all`);
      const d = res.data || {};
      alert(
        `✅ Enrolled: ${d.enrolled}\n` +
        `✉️  Welcome emails sent: ${d.welcome_emails_sent || 0}\n` +
        `⏭  Skipped (declined): ${d.skipped}\n` +
        `❌ Errors: ${d.errors}\n\n` +
        `Total applications processed: ${d.total_applications}`
      );
      // Refresh data
      const [vobRes, approvedRes] = await Promise.all([
        axios.get(`${API}/choir/vob-applications`),
        axios.get(`${API}/choir/approved-members`),
      ]);
      setVobApplications(vobRes.data || []);
      setApprovedMembers(approvedRes.data || []);
    } catch (error) {
      console.error('Bulk enroll error:', error);
      alert(error.response?.data?.detail || 'Failed to bulk enroll applications');
    }
    setBulkEnrolling(false);
  };

  // Rehearsal management
  const saveRehearsal = async () => {
    if (!rehearsalDraft.date) return;
    try {
      const res = await axios.post(`${API}/choir/rehearsals`, rehearsalDraft);
      setRehearsals([res.data, ...rehearsals]);
      setRehearsalDraft({ date: '', time: '', location: '', notes: '' });
      setShowRehearsalForm(false);
    } catch (error) {
      console.error('Error saving rehearsal:', error);
    }
  };

  const deleteRehearsal = async (id) => {
    if (!window.confirm('Delete this rehearsal?')) return;
    try {
      await axios.delete(`${API}/choir/rehearsals/${id}`);
      setRehearsals(rehearsals.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error deleting rehearsal:', error);
    }
  };

  // Song management
  const saveSong = async () => {
    if (!songDraft.title) return;
    try {
      if (editingSong) {
        const res = await axios.put(`${API}/choir/songs/${editingSong.id}`, songDraft);
        setSongs(songs.map(s => s.id === editingSong.id ? res.data : s));
      } else {
        const res = await axios.post(`${API}/choir/songs`, songDraft);
        setSongs([res.data, ...songs]);
      }
      setSongDraft({ title: '', composer: '', key: '', lead: '', notes: '', lyrics: '' });
      setShowSongForm(false);
      setEditingSong(null);
    } catch (error) {
      console.error('Error saving song:', error);
    }
  };

  const deleteSong = async (id) => {
    if (!window.confirm('Delete this song?')) return;
    try {
      await axios.delete(`${API}/choir/songs/${id}`);
      setSongs(songs.filter(s => s.id !== id));
    } catch (error) {
      console.error('Error deleting song:', error);
    }
  };

  // Hymn management
  const saveHymn = async () => {
    if (!hymnDraft.number || !hymnDraft.title) {
      alert('Please enter hymn number and title');
      return;
    }
    try {
      const verses = hymnDraft.verses.split('\n\n').filter(v => v.trim());
      await axios.post(`${API}/choir/hymns`, {
        number: parseInt(hymnDraft.number),
        title: hymnDraft.title,
        first_line: hymnDraft.first_line,
        verses: verses,
        language: hymnDraft.language
      });
      setHymnDraft({ number: '', title: '', first_line: '', verses: '', language: 'english' });
      setShowHymnForm(false);
      loadHymns();
      alert('Hymn added successfully!');
    } catch (error) {
      console.error('Error saving hymn:', error);
      alert(error.response?.data?.detail || 'Error saving hymn');
    }
  };

  // Hymn selection
  const toggleHymnSelection = (hymn) => {
    const isSelected = selectedHymns.some(h => h.number === hymn.number);
    if (isSelected) {
      setSelectedHymns(selectedHymns.filter(h => h.number !== hymn.number));
    } else if (selectedHymns.length < requiredHymnCount) {
      setSelectedHymns([...selectedHymns, hymn]);
    }
  };

  const saveHymnSelection = async () => {
    if (!serviceDate || selectedHymns.length !== requiredHymnCount) {
      alert(`Please select a date and ${requiredHymnCount} hymns`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/choir/service-hymns`, {
        service_type: serviceType,
        service_date: serviceDate,
        hymn_numbers: selectedHymns.map(h => h.number),
        notes: ''
      });
      const res = await axios.get(`${API}/choir/service-hymns`);
      setSelections(res.data);
      setSelectedHymns([]);
      setServiceDate('');
      alert('Hymns saved for service!');
    } catch (error) {
      console.error('Error saving selection:', error);
      alert(error.response?.data?.detail || 'Error saving');
    }
    setSaving(false);
  };

  const deleteSelection = async (id) => {
    if (!window.confirm('Delete this hymn selection?')) return;
    try {
      await axios.delete(`${API}/choir/service-hymns/${id}`);
      setSelections(selections.filter(s => s.id !== id));
    } catch (error) {
      console.error('Error deleting selection:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const pendingMembers = members.filter(m => m.status === 'pending');
  const legacyApprovedMembers = members.filter(m => m.status === 'approved');

  return (
    <div className="space-y-6">
      {/* Master Password Rotation Modal (Main Admin only) */}
      {masterPasswordDue && !masterPasswordModalDismissed && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="master-password-due-modal"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            <div className="px-6 py-5 bg-gradient-to-r from-red-600 via-rose-600 to-pink-600 text-white flex items-center gap-3">
              <div className="p-2 rounded-xl bg-white/15"><ShieldCheck className="w-6 h-6" /></div>
              <div>
                <h3 className="text-lg font-bold">Choir Password Rotation Due</h3>
                <p className="text-rose-100 text-sm">Time to set a new master password for all choir members.</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700 leading-relaxed">
                The choir master password was last changed{' '}
                <strong>{masterPasswordStatus?.days_since_change ?? '—'} day{masterPasswordStatus?.days_since_change === 1 ? '' : 's'} ago</strong>.
                For security, please rotate it now. Every approved choir member will be emailed the new password.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900">
                <strong>{masterPasswordStatus?.choir_count ?? 0}</strong> choir member{masterPasswordStatus?.choir_count === 1 ? '' : 's'} will receive the new password by email.
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <button
                  onClick={() => setMasterPasswordModalDismissed(true)}
                  data-testid="master-password-modal-dismiss"
                  className="flex-1 px-4 py-3 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200"
                >
                  Later
                </button>
                <button
                  onClick={() => { setActiveTab('master-password'); setMasterPasswordModalDismissed(true); }}
                  data-testid="master-password-modal-open"
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold shadow-lg hover:from-violet-700 hover:to-fuchsia-700"
                >
                  Change Password Now
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-white/20">
            <Music className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Choir Ministry</h2>
            <p className="text-purple-100">Manage choir members, hymns, songs & rehearsals</p>
          </div>
          {stats && (
            <div className="ml-auto flex gap-3">
              <div className="px-4 py-2 rounded-xl bg-white/20 text-center">
                <p className="text-xs text-purple-200">Members</p>
                <p className="text-xl font-bold">{stats.approved_members}</p>
              </div>
              <div className="px-4 py-2 rounded-xl bg-white/20 text-center">
                <p className="text-xs text-purple-200">Pending</p>
                <p className="text-xl font-bold">{stats.pending_applications}</p>
              </div>
              <div className="px-4 py-2 rounded-xl bg-white/20 text-center">
                <p className="text-xs text-purple-200">Hymns</p>
                <p className="text-xl font-bold">{stats.unique_hymns}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl overflow-x-auto">
        {[
          { id: 'approved', label: 'Approved Members', icon: UserCheck, badge: approvedMembers.length },
          { id: 'vob-forms', label: 'VOB Form Applications', icon: FileText, badge: pendingMembers.length },
          { id: 'events', label: 'Events & QR', icon: QrCode, alert: choirAlerts.length > 0 },
          { id: 'attendance', label: 'Attendance', icon: Award },
          ...(isMainAdmin ? [{ id: 'master-password', label: 'Master Password', icon: ShieldCheck, alert: masterPasswordDue }] : []),
          { id: 'rehearsals', label: 'Rehearsals', icon: Calendar },
          { id: 'songs', label: 'Song Library', icon: FileText },
          { id: 'hymns', label: 'Hymn Selection', icon: BookOpen },
          { id: 'history', label: 'Selection History', icon: Clock },
          { id: 'email', label: 'Email', icon: Mail },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`choir-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id ? 'bg-white text-purple-700 shadow' : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-violet-600 text-white text-xs">{tab.badge}</span>
            )}
            {tab.alert && (
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            )}
          </button>
        ))}
      </div>

      {/* Choir Alerts Banner — appears on any tab */}
      {choirAlerts.length > 0 && activeTab !== 'attendance' && activeTab !== 'events' && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-start gap-3" data-testid="choir-alerts-banner">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-red-900">
              {choirAlerts.length} choir member{choirAlerts.length > 1 ? 's have' : ' has'} missed the last 2 events
            </p>
            <p className="text-sm text-red-700 mt-1">
              {choirAlerts.slice(0, 3).map(a => a.member_name).join(', ')}
              {choirAlerts.length > 3 && ` +${choirAlerts.length - 3} more`}
            </p>
            <button
              onClick={() => setActiveTab('attendance')}
              className="text-sm text-red-700 underline mt-2 hover:text-red-900"
            >
              View details →
            </button>
          </div>
        </div>
      )}

      {/* APPROVED MEMBERS TAB */}
      {activeTab === 'approved' && (
        <div className="space-y-4" data-testid="approved-members-panel">
          <div className="bg-gradient-to-r from-violet-50 to-amber-50 rounded-2xl p-5 border-2 border-violet-200">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-bold text-violet-900 text-lg">Choir Approved Members</h3>
                <p className="text-sm text-slate-600 mt-1">People who have been approved and are enrolled in the choir. Their attendance is tracked.</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-black text-violet-800">{approvedMembers.length}</p>
                <p className="text-xs text-slate-500">enrolled</p>
              </div>
            </div>
          </div>

          {approvedMembers.length === 0 ? (
            <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-200">
              <UserCheck className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600">No choir members approved yet.</p>
              <p className="text-sm text-slate-500 mt-1">Approve a VOB Form Application to enroll them here.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow border overflow-hidden">
              <table className="w-full">
                <thead className="bg-violet-50">
                  <tr>
                    <th className="text-left text-xs font-bold text-violet-900 uppercase tracking-wider px-4 py-3">Name</th>
                    <th className="text-left text-xs font-bold text-violet-900 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Email</th>
                    <th className="text-left text-xs font-bold text-violet-900 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Departments</th>
                    <th className="text-center text-xs font-bold text-violet-900 uppercase tracking-wider px-4 py-3">Attendance</th>
                    <th className="text-center text-xs font-bold text-violet-900 uppercase tracking-wider px-4 py-3 hidden lg:table-cell" title="Was the most recent master-password email delivered to this member?">Password Email</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {approvedMembers.map(m => (
                    <tr key={m.id} className="hover:bg-violet-50/30" data-testid={`approved-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-violet-950 font-bold text-sm flex-shrink-0">
                            {(m.full_name || '').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-800">{m.full_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden sm:table-cell">{m.email || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden md:table-cell">{(m.departments || []).join(', ')}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className={`font-bold ${m.attendance_rate >= 75 ? 'text-green-600' : m.attendance_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                            {m.attended_count}/{m.total_events}
                          </span>
                          <span className="text-[10px] text-slate-500">{m.attendance_rate}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center hidden lg:table-cell" data-testid={`password-email-status-${m.id}`}>
                        <PasswordEmailBadge member={m} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Remove ${m.full_name} from the choir?`)) return;
                            try {
                              await axios.delete(`${API}/choir/approved-members/${m.id}`);
                              await refreshChoirData();
                            } catch { alert('Failed to remove'); }
                          }}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                          data-testid={`remove-approved-${m.id}`}
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* EVENTS & QR TAB */}
      {activeTab === 'events' && (
        <ChoirEventsTab
          venueToken={venueToken}
          rotatingVenueToken={rotatingVenueToken}
          onRotateVenueToken={rotateVenueToken}
          newEventType={newEventType}
          setNewEventType={setNewEventType}
          newEventDate={newEventDate}
          setNewEventDate={setNewEventDate}
          newEventTime={newEventTime}
          setNewEventTime={setNewEventTime}
          newEventTitle={newEventTitle}
          setNewEventTitle={setNewEventTitle}
          eventCreating={eventCreating}
          onCreateEvent={createChoirEvent}
          choirEvents={choirEvents}
          onViewEventAttendance={viewEventAttendance}
          onDeleteEvent={deleteChoirEvent}
          onSendEventReminder={sendEventReminder}
          isMainAdmin={isMainAdmin}
        />
      )}

      {/* ATTENDANCE TAB */}
      {activeTab === 'attendance' && (
        <ChoirAttendanceTab
          choirAlerts={choirAlerts}
          approvedMembers={approvedMembers}
          selectedEventAttendance={selectedEventAttendance}
          eventAttendanceRecords={eventAttendanceRecords}
          onCloseEventAttendance={() => setSelectedEventAttendance(null)}
          onDownloadAttendanceReport={downloadAttendanceReport}
        />
      )}

      {/* MASTER PASSWORD TAB (Main Admin only) */}
      {activeTab === 'master-password' && (
        <ChoirMasterPasswordTab isMainAdmin={isMainAdmin} />
      )}


      {/* VOB FORM APPLICATIONS TAB */}
      {activeTab === 'vob-forms' && (
        <ChoirVobApplicationsTab
          vobApplications={vobApplications}
          isMainAdmin={isMainAdmin}
          bulkEnrolling={bulkEnrolling}
          onApprove={approveVobApp}
          onDecline={declineVobApp}
          onDelete={deleteVobApp}
          onBulkEnroll={bulkEnrollAllVobApps}
        />
      )}

      {/* REHEARSALS TAB */}
      {activeTab === 'rehearsals' && (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" /> Rehearsal Schedule
            </h3>
            <button onClick={() => setShowRehearsalForm(!showRehearsalForm)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-100 text-purple-700 font-medium hover:bg-purple-200">
              <Plus className="w-4 h-4" /> Add Rehearsal
            </button>
          </div>

          <AnimatePresence>
            {showRehearsalForm && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 p-4 rounded-xl bg-purple-50 border border-purple-100">
                <div className="grid md:grid-cols-4 gap-3 mb-4">
                  <input type="date" className="border-2 border-purple-200 rounded-xl px-4 py-2 focus:border-purple-400 outline-none" value={rehearsalDraft.date} onChange={e => setRehearsalDraft(d => ({...d, date: e.target.value}))} />
                  <input type="time" className="border-2 border-purple-200 rounded-xl px-4 py-2 focus:border-purple-400 outline-none" value={rehearsalDraft.time} onChange={e => setRehearsalDraft(d => ({...d, time: e.target.value}))} />
                  <input placeholder="Location" className="border-2 border-purple-200 rounded-xl px-4 py-2 focus:border-purple-400 outline-none" value={rehearsalDraft.location} onChange={e => setRehearsalDraft(d => ({...d, location: e.target.value}))} />
                  <input placeholder="Notes" className="border-2 border-purple-200 rounded-xl px-4 py-2 focus:border-purple-400 outline-none" value={rehearsalDraft.notes} onChange={e => setRehearsalDraft(d => ({...d, notes: e.target.value}))} />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveRehearsal} className="px-4 py-2 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700">Save</button>
                  <button onClick={() => setShowRehearsalForm(false)} className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 font-medium">Cancel</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {rehearsals.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No rehearsals scheduled</p>
          ) : (
            <div className="space-y-3">
              {rehearsals.map(reh => (
                <div key={reh.id} className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-100">
                  <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-slate-800">{reh.date}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      {reh.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{reh.time}</span>}
                      {reh.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{reh.location}</span>}
                    </div>
                    {reh.notes && <p className="text-sm text-slate-500 mt-1">{reh.notes}</p>}
                  </div>
                  <button onClick={() => deleteRehearsal(reh.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SONGS TAB */}
      {activeTab === 'songs' && (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" /> Song Library
            </h3>
            <button onClick={() => { setShowSongForm(!showSongForm); setEditingSong(null); setSongDraft({ title: '', composer: '', key: '', lead: '', notes: '', lyrics: '' }); }} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-100 text-indigo-700 font-medium hover:bg-indigo-200">
              <Plus className="w-4 h-4" /> Add Song
            </button>
          </div>

          <AnimatePresence>
            {showSongForm && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 p-4 rounded-xl bg-indigo-50 border border-indigo-100">
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <input placeholder="Song Title *" className="border-2 border-indigo-200 rounded-xl px-4 py-2 focus:border-indigo-400 outline-none" value={songDraft.title} onChange={e => setSongDraft(d => ({...d, title: e.target.value}))} />
                  <input placeholder="Composer" className="border-2 border-indigo-200 rounded-xl px-4 py-2 focus:border-indigo-400 outline-none" value={songDraft.composer} onChange={e => setSongDraft(d => ({...d, composer: e.target.value}))} />
                  <input placeholder="Key (e.g., C Major)" className="border-2 border-indigo-200 rounded-xl px-4 py-2 focus:border-indigo-400 outline-none" value={songDraft.key} onChange={e => setSongDraft(d => ({...d, key: e.target.value}))} />
                  <input placeholder="Lead Singer" className="border-2 border-indigo-200 rounded-xl px-4 py-2 focus:border-indigo-400 outline-none" value={songDraft.lead} onChange={e => setSongDraft(d => ({...d, lead: e.target.value}))} />
                </div>
                <textarea placeholder="Lyrics (optional)" className="w-full border-2 border-indigo-200 rounded-xl px-4 py-2 focus:border-indigo-400 outline-none mb-3" rows={3} value={songDraft.lyrics} onChange={e => setSongDraft(d => ({...d, lyrics: e.target.value}))} />
                <input placeholder="Notes" className="w-full border-2 border-indigo-200 rounded-xl px-4 py-2 focus:border-indigo-400 outline-none mb-3" value={songDraft.notes} onChange={e => setSongDraft(d => ({...d, notes: e.target.value}))} />
                <div className="flex gap-2">
                  <button onClick={saveSong} className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-700">{editingSong ? 'Update' : 'Save'}</button>
                  <button onClick={() => { setShowSongForm(false); setEditingSong(null); }} className="px-4 py-2 rounded-xl bg-slate-200 text-slate-700 font-medium">Cancel</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {songs.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No songs in library</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {songs.map(song => (
                <div key={song.id} className="p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 hover:shadow-lg transition-all group">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-gradient-to-r from-indigo-500 to-blue-500">
                        <Music className="w-4 h-4 text-white" />
                      </div>
                      <h4 className="font-bold text-slate-800">{song.title}</h4>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { setEditingSong(song); setSongDraft(song); setShowSongForm(true); }} className="p-1 rounded-lg text-blue-500 hover:bg-blue-50">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteSong(song.id)} className="p-1 rounded-lg text-red-500 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {song.composer && <p className="text-sm text-slate-600 mb-1">By: {song.composer}</p>}
                  {song.lead && <p className="text-sm text-slate-500 mb-1">Lead: {song.lead}</p>}
                  {song.key && <span className="inline-block px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">Key: {song.key}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HYMNS TAB */}
      {activeTab === 'hymns' && (
        <ChoirHymnsTab
          hymns={hymns}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          isMainAdmin={isMainAdmin}
          showHymnForm={showHymnForm}
          setShowHymnForm={setShowHymnForm}
          hymnDraft={hymnDraft}
          setHymnDraft={setHymnDraft}
          onSaveHymn={saveHymn}
          serviceTypes={SERVICE_TYPES}
          serviceType={serviceType}
          setServiceType={setServiceType}
          serviceDate={serviceDate}
          setServiceDate={setServiceDate}
          selectedHymns={selectedHymns}
          setSelectedHymns={setSelectedHymns}
          requiredHymnCount={requiredHymnCount}
          onToggleHymnSelection={toggleHymnSelection}
          onSaveHymnSelection={saveHymnSelection}
          saving={saving}
          onHymnsChanged={loadHymns}
        />
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-bold text-slate-800">Selection History</h3>
          </div>
          {selections.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No selections yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {selections.map(sel => {
                const typeConfig = SERVICE_TYPES.find(t => t.value === sel.service_type);
                return (
                  <div key={sel.id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-purple-100">
                          <Calendar className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{sel.service_date}</p>
                          <p className="text-sm text-slate-500">{typeConfig?.label || sel.service_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-600">{sel.hymn_numbers?.length || 0} hymns</p>
                          <p className="text-xs text-slate-400">Hymn No {sel.hymn_numbers?.join(', Hymn No ')}</p>
                        </div>
                        <button onClick={() => deleteSelection(sel.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Email Tab */}
      {activeTab === 'email' && (
        <DepartmentEmailModule department="choir" adminUser={adminUser} compact={false} />
      )}
    </div>
  );
}

export default ChoirMinistrySection;
